/**
 * Centralised BigQuery analytics for the SingleComment subtree.
 *
 * Mirrors the `useCommentRpaAnalytics` pattern: a single `fireEvent`
 * builds the common envelope (patient/order/org/user/screen/payer) and
 * each public method only contributes its event-specific name and extras.
 *
 * @module busniessOffice/components/singleComment/hooks/useBoCommentAnalytics
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../../../../context/payerNameContextFromSingleOrderLayout";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../../../../enums/eventsData";
import { EventsName } from "../../../../../../../enums/eventsName";
import { RootState } from "../../../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../../../../workerManagers/workerManager";

/** Debounce window applied to the high-frequency BUSINESS_OFFICE_EDITED event. */
const WRITE_BACK_EDIT_DEBOUNCE_MS = 850;

/**
 * Build a stable set of analytics loggers for one BO comment row.
 *
 * @param generalOrderDetails - Source of order/patient identifiers and `bo_status`.
 * @param authOnFile - Provides `auth_status` for relevant events.
 * @param index - Zero-based row index used to disambiguate the event name.
 * @returns Named loggers for each user interaction.
 */
export const useBoCommentAnalytics = (
  generalOrderDetails: GeneralOrderDetails | undefined,
  authOnFile: AuthOnFile | undefined,
  index: number,
) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const orgId = getOrgIdForFetchExternalWorklist() || "";
  const patientId = generalOrderDetails?.demographics?.patient_id ?? "";
  const orderId = generalOrderDetails?.order_id ?? "";
  const userId = user?.id ?? "";
  const userEmail = user?.email ?? "";
  const authStatus = authOnFile?.auth_status ?? "";
  const boStatusFromOrder = generalOrderDetails?.status?.bo_status ?? "";
  const coverageStatus = generalOrderDetails?.status?.ev_bv?.primary ?? "";
  const indexLabel = `${index + 1}`;

  const fireEvent = useCallback(
    (eventName: string, additionalData: Record<string, unknown> = {}) => {
      apiWorkerManager.enqueueAndProcess({
        patient_id: patientId,
        order_id: orderId,
        org_id: orgId,
        user_id: userId,
        user_email: userEmail,
        event_name: eventName,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
          payer_name: payerName,
          ...additionalData,
        },
      });
    },
    [patientId, orderId, orgId, userId, userEmail, payerName],
  );

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return useMemo(
    () => ({
      /** RADIO_BUTTON_SELECTED — fired when the BO status badge is changed. */
      logBoStatusSelected: ({
        currentValue,
        newValue,
      }: {
        currentValue: string;
        newValue: string;
      }) => {
        fireEvent(
          `${EventsName.RADIO_BUTTON_SELECTED} (${indexLabel} - ${newValue})`,
          {
            current_value: currentValue,
            old_value: boStatusFromOrder,
            new_value: newValue,
          },
        );
      },

      /** RADIO_BUTTON_CLICKED — fired when the BO status badge is opened. */
      logBoStatusDefaultClick: ({ currentValue }: { currentValue: string }) => {
        fireEvent(`${EventsName.RADIO_BUTTON_CLICKED} (${indexLabel})`, {
          current_value: currentValue,
        });
      },

      /** CLICKED_ON_BUSINESS_OFFICE — fired when the write-back panel is clicked. */
      logWriteBackContainerClicked: ({ jCodes }: { jCodes: string }) => {
        fireEvent(`${EventsName.CLICKED_ON_BUSINESS_OFFICE} (${indexLabel})`, {
          index,
          auth_status: authStatus,
          j_code: jCodes,
        });
      },

      /**
       * BUSINESS_OFFICE_EDITED — fires after the user pauses typing for
       * {@link WRITE_BACK_EDIT_DEBOUNCE_MS}ms. Caller must pass the previous
       * value captured at mount/last-fire time.
       */
      logWriteBackEdited: ({
        newValue,
        oldValue,
        jCodes,
      }: {
        newValue: string;
        oldValue: string;
        jCodes: string;
      }) => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
          fireEvent(`${EventsName.BUSINESS_OFFICE_EDITED} (${indexLabel})`, {
            new_value: newValue,
            old_value: oldValue,
            auth_status: authStatus,
            coverage_status: coverageStatus,
            j_code: jCodes,
          });
          debounceTimeoutRef.current = null;
        }, WRITE_BACK_EDIT_DEBOUNCE_MS);
      },
    }),
    [
      fireEvent,
      indexLabel,
      index,
      authStatus,
      coverageStatus,
      boStatusFromOrder,
    ],
  );
};
